// Organization/Team Management API module
import { BaseApiClient } from './BaseApiClient';
import type { ApiResponse } from '@/types/api';
import type { PaginationParams } from '@/types/admin';
import type { Organization, TeamInvitation, TeamMember } from '@/types/team';

interface OrganizationListResponse {
  organizations: Organization[];
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    total_count?: number;
    totalCount?: number;
    [key: string]: unknown;
  };
}

interface OrganizationDetailsResponse {
  organization: Organization;
  members?: TeamMember[];
  invitations?: TeamInvitation[];
  stats?: {
    pending_invitations?: number;
    [key: string]: unknown;
  };
}

export class OrganizationApi extends BaseApiClient {
  constructor() {
    super('OrganizationAPI');
  }

  async getOrganizations(
    filters?: { search?: string; tier?: string; owner_id?: string },
    pagination?: PaginationParams
  ): Promise<OrganizationListResponse> {
    const params = { ...filters, ...pagination };
    const response = await this.api.get<OrganizationListResponse>('/organizations', { params });
    return response.data;
  }

  async getOrganizationDetails(orgId: string): Promise<OrganizationDetailsResponse> {
    const response = await this.api.get<OrganizationDetailsResponse>(`/organizations/${orgId}`);
    return response.data;
  }

  async suspendTeam(orgId: string): Promise<ApiResponse<Record<string, unknown>>> {
    this.logger.info(`Suspending team ${orgId} (scheduling deletion at end of billing cycle)`);
    const response = await this.api.delete<ApiResponse<Record<string, unknown>>>(`/organizations/${orgId}/suspend`);
    return response.data;
  }

  async deleteTeamImmediately(orgId: string): Promise<ApiResponse<Record<string, unknown>>> {
    this.logger.info(`Immediately deleting team ${orgId}`);
    const response = await this.api.delete<ApiResponse<Record<string, unknown>>>(`/organizations/${orgId}/execute-deletion`);
    return response.data;
  }
}

